import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { TextField, Dialog, DialogActions, DialogTitle, DialogContent } from '@material-ui/core';
import { DialogButton, ComponentController, InformationMessage } from 'components/common';
import { MaxAddressLength, MaxPayeeLengthIndividual, MaxPostCodeLength, PayeeFormNames, OrganisationDataSources, MaxCityLength } from '../Helpers';

const AddNewIndividual = ({
  popupState,
  requestData,
  setPopupState,
  setFormState,
  setValidationValue
}) => {
  const { control, handleSubmit, watch, formState: { errors } } = useForm({
    mode: "onTouched",
    defaultValues: {
      givenName: popupState?.newValue,
      addressLine1: requestData?.address,
      postCode: requestData?.postCode
    }
  });

  const addressLine1Value = watch("addressLine1");
  const addressLine2Value = watch("addressLine2");
  const addressLine3Value = watch("addressLine3");
  const cityValue = watch("city");
  const givenNameValue = watch("givenName");
  const familyNameValue = watch("familyName");
  const postCodeValue = watch("postCode");

  const handleClose = useCallback(
    () => setPopupState(previous => ({ ...previous, show: false })),
    [setPopupState]);

  const onSubmit = useCallback(async popupData => {
    handleClose();
    const payee = `${popupData?.givenName} ${popupData?.familyName}`;
    const address = [popupData?.addressLine1, popupData?.addressLine2, popupData?.addressLine3].filter(Boolean).join(' ');
    setFormState((previous) => {
      return {
        ...previous,
        ...popupData,
        payee: payee,
        address: address,
        source: OrganisationDataSources.New 
      }
    });
    setValidationValue("payee", payee, { shouldValidate: true });
  }, [handleClose, setFormState]);

  return (
    <Dialog open={popupState.show} onClose={handleClose} aria-labelledby="assured-dialog-title">
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle id="assured-dialog-title">Add a new payee</DialogTitle>
        <DialogContent>
          <ComponentController
            name="givenName"
            control={control}
            required
            rules={{ maxLength: MaxPayeeLengthIndividual }}
            render={({ field: { name, ...rest } }) =>
              <TextField
                {...rest}
                id={name}
                name={name}
                label={PayeeFormNames[name]}
                margin="dense"
                fullWidth
                error={!!errors[name]}
                helperText={<InformationMessage
                  fieldName={PayeeFormNames[name]}
                  fieldErrors={errors[name]}
                  maxLength={MaxPayeeLengthIndividual}
                  showLengthInfo={true}
                  length={givenNameValue?.length || 0}
                />}
              />
            }
          />
          <ComponentController
            name="familyName"
            control={control}
            required
            rules={{ maxLength: MaxPayeeLengthIndividual }}
            render={({ field: { name, ...rest } }) =>
              <TextField
                {...rest}
                id={name}
                name={name}
                label={PayeeFormNames[name]}
                margin="dense"
                fullWidth
                error={!!errors[name]}
                helperText={<InformationMessage
                  fieldName={PayeeFormNames[name]}
                  fieldErrors={errors[name]}
                  maxLength={MaxPayeeLengthIndividual}
                  showLengthInfo={true}
                  length={familyNameValue?.length || 0}
                />}
              />
            }
          />
          <ComponentController
            name="addressLine1"
            control={control}
            required
            rules={{ maxLength: MaxAddressLength }}
            render={({ field: { name, ...rest } }) =>
              <TextField
                {...rest}
                id={name}
                name={name}
                label={PayeeFormNames[name]}
                margin="dense"
                fullWidth
                error={!!errors[name]}
                helperText={<InformationMessage
                  fieldName={PayeeFormNames[name]}
                  fieldErrors={errors[name]}
                  maxLength={MaxAddressLength}
                  showLengthInfo={true}
                  length={addressLine1Value?.length || 0}
                />}
              />
            }
          />
          <ComponentController
            name="addressLine2"
            control={control}
            required
            rules={{ maxLength: MaxAddressLength }}
            render={({ field: { name, ...rest } }) =>
              <TextField
                {...rest}
                id={name}
                name={name}
                label={PayeeFormNames[name]}
                margin="dense"
                fullWidth
                error={!!errors[name]}
                helperText={<InformationMessage
                  fieldName={PayeeFormNames[name]}
                  fieldErrors={errors[name]}
                  maxLength={MaxAddressLength}
                  showLengthInfo={true}
                  length={addressLine2Value?.length || 0}
                />}
              />
            }
          />
          <ComponentController
            name="addressLine3"
            control={control}
            rules={{ maxLength: MaxAddressLength }}
            render={({ field: { name, ...rest } }) =>
              <TextField
                {...rest}
                id={name}
                name={name}
                label={PayeeFormNames[name]}
                margin="dense"
                fullWidth
                error={!!errors[name]}
                helperText={<InformationMessage
                  fieldName={PayeeFormNames[name]}
                  fieldErrors={errors[name]}
                  maxLength={MaxAddressLength}
                  showLengthInfo={true}
                  length={addressLine3Value?.length || 0}
                />}
              />
            }
          />
          <ComponentController
            name="city"
            control={control}
            required
            rules={{ maxLength: MaxCityLength }}
            render={({ field: { name, ...rest } }) =>
              <TextField
                {...rest}
                id={name}
                name={name}
                label={PayeeFormNames[name]}
                margin="dense"
                fullWidth
                error={!!errors[name]}
                helperText={<InformationMessage
                  fieldName={PayeeFormNames[name]}
                  fieldErrors={errors[name]}
                  maxLength={MaxCityLength}
                  showLengthInfo={true}
                  length={cityValue?.length || 0}
                />}
              />
            }
          />
          <ComponentController
            name="postCode"
            control={control}
            required
            rules={{ maxLength: MaxPostCodeLength }}
            render={({ field: { name, ...rest } }) =>
              <TextField
                {...rest}
                id={name}
                name={name}
                label={PayeeFormNames[name]}
                margin="dense"
                fullWidth
                error={!!errors[name]}
                helperText={<InformationMessage
                  fieldName={PayeeFormNames[name]}
                  fieldErrors={errors[name]}
                  maxLength={MaxPostCodeLength}
                  showLengthInfo={true}
                  length={postCodeValue?.length || 0}
                />}
              />
            }
          />
        </DialogContent>
        <DialogActions className="dialog-buttons">
          <DialogButton onClick={handleClose}>Cancel</DialogButton>
          <DialogButton type="Submit">Add</DialogButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddNewIndividual;